import { z } from "zod";

export const shippingDestinationSchema = z.enum([
  "la_paz_el_alto",
  "santa_cruz",
  "cochabamba",
  "sucre",
  "tarija",
  "beni",
  "oruro",
  "potosi",
  "pando",
]);

export type ShippingDestination = z.infer<typeof shippingDestinationSchema>;

export const defaultShippingDestination: ShippingDestination = "la_paz_el_alto";

export interface ShippingDestinationOption {
  readonly key: ShippingDestination;
  readonly label: string;
  readonly managementCity: string;
}

// managementCity is the destination name the management system can resolve
// against its `public.cities` catalog
// (Administrador de inventario/supabase/migrations/20260409110000_customer_cities_catalog.sql).
// "Beni", "Pando", "Santa Cruz" and "Potosí" are departments, not city rows, so
// the department literal is never sent as the management destination: Beni maps
// to Trinidad and Pando maps to Cobija.
export const shippingDestinationOptions: readonly ShippingDestinationOption[] = [
  { key: "la_paz_el_alto", label: "La Paz/El Alto", managementCity: "La Paz" },
  { key: "santa_cruz", label: "Santa Cruz", managementCity: "Santa Cruz de la Sierra" },
  { key: "cochabamba", label: "Cochabamba", managementCity: "Cochabamba" },
  { key: "sucre", label: "Sucre", managementCity: "Sucre" },
  { key: "tarija", label: "Tarija", managementCity: "Tarija" },
  { key: "beni", label: "Beni", managementCity: "Trinidad" },
  { key: "oruro", label: "Oruro", managementCity: "Oruro" },
  // The user-facing label is accented; the management city stays unaccented,
  // matching the `public.cities` row.
  { key: "potosi", label: "Potosí", managementCity: "Potosi" },
  { key: "pando", label: "Pando", managementCity: "Cobija" },
];

const shippingDestinationOptionByKey = new Map<string, ShippingDestinationOption>(
  shippingDestinationOptions.map((option) => [option.key, option]),
);

export function resolveShippingDestinationOption(
  destination: string,
): ShippingDestinationOption {
  const option = shippingDestinationOptionByKey.get(destination);

  if (!option) {
    throw new Error(`Unknown shipping destination: ${destination}`);
  }

  return option;
}
